import { memo, useCallback } from "react";
import ConnectionIcon from "./ConnectionIcon";
import MCPItemActions from "./MCPItemActions";
import MCPStatusBadge from "./MCPStatusBadge";
import { Switch } from "@/components/ui/switch";
import { useMCPConnectionTest } from "../hooks/useMCPConnectionTest";
import { getMCPStatusState } from "../status";
import type { MCPConnection } from "../types";
import { useTranslation } from "react-i18next";

type Props = {
  connection: MCPConnection;
  isSelected: boolean;
  canManage: boolean;
  onSelect: (id: string) => void;
  onEdit: (id: string) => void;
  onDelete: (id: string) => void;
  onToggleActive: (id: string, isActive: boolean) => void;
  isUpdatingActive?: boolean;
};

const MCPListItem = ({
  connection,
  isSelected,
  canManage,
  onSelect,
  onEdit,
  onDelete,
  onToggleActive,
  isUpdatingActive = false,
}: Props) => {
  const { t } = useTranslation();
  const handleSelect = useCallback(
    () => onSelect(connection.id),
    [connection.id, onSelect],
  );

  const handleEdit = useCallback(
    () => onEdit(connection.id),
    [connection.id, onEdit],
  );

  const handleDelete = useCallback(
    () => onDelete(connection.id),
    [connection.id, onDelete],
  );

  // Each row subscribes to its own test query. The shared cache means the detail
  // pane reuses this result instantly when the user opens the row.
  const {
    status: liveStatus,
    errorMessage,
    authRequired,
    refetch: rerunTest,
  } = useMCPConnectionTest(connection.id);
  const { status } = getMCPStatusState({
    isActive: connection.status === "online",
    needsAttention: connection.needsAttention,
    authRequired,
    liveStatus,
  });
  const statusTitle =
    status === "offline"
      ? errorMessage ?? t("mcpPage.status.offline")
      : status === "disabled" || status === "needsAttention"
        ? t(`mcpPage.status.${status}`)
        : undefined;
  const isActive = connection.status === "online";
  const toggleLabel = t(
    isActive
      ? "mcpPage.actions.disableConnection"
      : "mcpPage.actions.enableConnection",
    { name: connection.name },
  );

  return (
    <li
      className={`group flex h-[57px] min-w-0 items-center gap-3 rounded-md border px-1 py-[7.5px] transition-colors ${
        isSelected
          ? "border-border bg-primary/10"
          : "border-transparent hover:border-border hover:bg-muted/30"
      }`}
    >
      <button
        type="button"
        onClick={handleSelect}
        className="flex min-w-0 flex-1 items-center gap-3 rounded-sm text-start outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
        aria-label={t("mcpPage.actions.viewConnection", {
          name: connection.name,
        })}
        aria-pressed={isSelected}
      >
        <span className="relative shrink-0">
          <ConnectionIcon connection={connection} size="sm" />
        </span>

        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm font-normal text-foreground">
            {connection.name}
          </span>
          <span className="block truncate text-xs text-muted-foreground">
            {connection.createdAt}
          </span>
        </span>

        <MCPStatusBadge
          status={status}
          title={statusTitle}
          className="max-w-32 shrink-0"
        />
      </button>

      <Switch
        checked={isActive}
        onCheckedChange={(checked) =>
          onToggleActive(connection.id, checked)
        }
        disabled={!canManage || isUpdatingActive}
        aria-label={toggleLabel}
        aria-busy={isUpdatingActive}
        className="h-[18px] w-[33px] border-0 p-px [&>span]:size-4 [&>span[data-state=checked]]:translate-x-[15px] rtl:[&>span[data-state=checked]]:-translate-x-[15px]"
      />

      <MCPItemActions
        canManage={canManage}
        onViewDetails={handleSelect}
        onEditDetails={handleEdit}
        onRefresh={rerunTest}
        onDelete={handleDelete}
      />
    </li>
  );
};

export default memo(MCPListItem);
